import { Box, Chip, ImageList, ImageListItem, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

import { Section, type SectionVariant } from "./Section";

type GalleryCategory = "mariage" | "bapteme" | "anniversaire" | "babyshower" | "entreprise";

interface GalleryItem {
  src: string;
  category: GalleryCategory;
}

const GALLERY_ITEMS: GalleryItem[] = [
  { src: "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&w=1200&q=80", category: "mariage" },
  { src: "https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?auto=format&fit=crop&w=1200&q=80", category: "mariage" },
  { src: "https://images.unsplash.com/photo-1520854221256-17451cc331bf?auto=format&fit=crop&w=1200&q=80", category: "bapteme" },
  { src: "https://images.unsplash.com/photo-1487956382158-bb926046304a?auto=format&fit=crop&w=1200&q=80", category: "bapteme" },
  { src: "https://images.unsplash.com/photo-1604017011826-d3b4c23f8914?auto=format&fit=crop&w=1200&q=80", category: "bapteme" },
  { src: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?auto=format&fit=crop&w=1200&q=80", category: "anniversaire" },
  { src: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1200&q=80", category: "anniversaire" },
  { src: "https://images.unsplash.com/photo-1519167758481-83f550bb49b3?auto=format&fit=crop&w=1200&q=80", category: "babyshower" },
  { src: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=1200&q=80", category: "entreprise" },
  { src: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?auto=format&fit=crop&w=1200&q=80", category: "mariage" },
  { src: "https://images.unsplash.com/photo-1529636798458-92182e662485?auto=format&fit=crop&w=1200&q=80", category: "entreprise" },
];

const FILTER_KEYS: ("all" | GalleryCategory)[] = [
  "all",
  "mariage",
  "bapteme",
  "anniversaire",
  "babyshower",
  "entreprise",
];

interface GallerySectionProps {
  variant?: SectionVariant;
}

export function GallerySection({ variant }: GallerySectionProps = {}) {
  const { t } = useTranslation(["landing", "common"]);
  const [activeFilter, setActiveFilter] = useState<"all" | GalleryCategory>("all");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const items = useMemo(
    () => (activeFilter === "all" ? GALLERY_ITEMS : GALLERY_ITEMS.filter((i) => i.category === activeFilter)),
    [activeFilter],
  );
  const altFor = (category: GalleryCategory) =>
    `${t("landing:gallery.title")} — ${t(`landing:gallery.filters.${category}`)}`;
  const slides = useMemo(
    () => items.map((i) => ({ src: i.src, alt: altFor(i.category) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, t],
  );

  return (
    <Section
      id="gallery"
      variant={variant}
      eyebrow={t("common:eyebrow.portfolio")}
      title={t("landing:gallery.title")}
      subtitle={t("landing:gallery.subtitle")}
    >
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 4 }}
      >
        {FILTER_KEYS.map((key) => (
          <Chip
            key={key}
            label={t(`landing:gallery.filters.${key}`)}
            variant={activeFilter === key ? "filled" : "outlined"}
            color={activeFilter === key ? "primary" : "default"}
            onClick={() => setActiveFilter(key)}
            sx={{ fontWeight: 500 }}
          />
        ))}
      </Stack>

      <Box>
        <ImageList variant="masonry" cols={3} gap={16} sx={{ m: 0 }}>
          {items.map((item, index) => (
            <ImageListItem
              key={item.src}
              onClick={() => setOpenIndex(index)}
              sx={{
                cursor: "pointer",
                borderRadius: 2,
                overflow: "hidden",
                transition: "transform 300ms ease",
                ":hover img": { transform: "scale(1.04)" },
              }}
            >
              <img
                src={`${item.src}`}
                alt={altFor(item.category)}
                loading="lazy"
                style={{ display: "block", width: "100%", transition: "transform 300ms ease" }}
              />
            </ImageListItem>
          ))}
        </ImageList>
      </Box>

      <Lightbox
        open={openIndex !== null}
        close={() => setOpenIndex(null)}
        index={openIndex ?? 0}
        slides={slides}
      />
    </Section>
  );
}
